import { useCountByStatus } from "@/api/bibliography/bibliography";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { PagingParameters } from "@/api/dtos";
import {
  bibliographyColumns,
  bibliographyColumnsVisibility,
} from "@/config/dashboard/bibliography";
import { generateTable } from "@/config/dashboard/general";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Bibliography } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { BibliographyLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * BibliographyLinks component
 * @returns {JSX.Element}
 */
const BibliographyLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [
    getPaginationRelatedBibliographies,
    setPaginationRelatedBibliographies,
  ] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedBibliography",
    getPaginationRelatedBibliographies.pageSize,
    getPaginationRelatedBibliographies.page *
      getPaginationRelatedBibliographies.pageSize,
  );

  const { data: countRelatedBibliographies } = useCountRelatedFields(id, type, [
    "relatedBibliography",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedBibliography",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedBibliography",
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedBibliographyId, setSelectedBibliographyId] = useState<
    string | null
  >(null);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(bibliographyColumns, "bibliography", t),
    [t],
  );

  const renderBibliographyRowActions = useCallback(
    (item: Bibliography) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="bibliography"
          previewElements={[
            {
              label: t("bibliographieDescriptionNumeroBibliographie"),
              value: item.bibliographieDescriptionNumeroBibliographie,
              type: "text",
            },
            {
              label: t("bibliographieDescriptionResumeLong"),
              value: item.bibliographieDescriptionResumeLong,
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: item.attachments,
              type: "image",
            },
          ]}
          onDelete={() => handleDeleteRelation(item.id)}
          onAdd={() => handleCreateRelation(item.id)}
        />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Bibliography>(({ row }) =>
        renderBibliographyRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderBibliographyRowActions]);

  const STORAGE_KEY = "bibliography-links-table";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedBibliographies,
    setPage: setPaginationRelatedBibliographies,
    totalSize: countRelatedBibliographies?.relatedBibliography ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedBibliographies")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelatedBibliographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: totalSize,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Bibliography) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderBibliographyRowActions(row)}>
                <BibliographyLinkGridCard
                item={row}
                t={t}
                onTextClick={() => setSelectedBibliographyId(row.id)}
              />
              </GridCardWithActions>
              <ShowElements
                type="bibliography"
                id={row.id}
                open={selectedBibliographyId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedBibliographyId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value: row.bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value: row.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default BibliographyLinks;
